import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Tooltip,
  ListSubheader,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

// Import các API services
import invoiceTemplateApi from "../../api/invoiceTemplateApi";
import customerApi from "../../api/customerApi";
import industryApi from "../../api/industryApi";

import InvoiceTemplateFilter from "./components/InvoiceTemplateFilter";
import InvoiceTemplateTable from "./components/InvoiceTemplateTable";
import AddEditInvoiceTemplateModal from "./components/AddEditInvoiceTemplateModal";

const pageNo = "InvoiceSignAndTemplatePage";
function InvoiceSignAndTemplatePage() {
  const user = useSelector((state) => state.auth.user);
  const roles = useSelector((state) => state.auth.roles);
  const customerInfor = useSelector((state) => state.auth.customerInfor);

  const [selectedCustomerId, setSelectedCustomerId] = useState(
    customerInfor.CusId,
  );
  const [searchKeyword, setSearchKeyword] = useState("");
  const [allCustomers, setAllCustomers] = useState([]);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // --- Trạng thái DataGrid và phân trang ---
  const [invoiceTemplates, setInvoiceTemplates] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // --- Trạng thái Modal thêm, sửa ký hiệu ---
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [currentTemplateToEdit, setCurrentTemplateToEdit] = useState(null);

  const [formErrors, setFormErrors] = useState({});

  // --- Trạng thái Modal xác nhận xóa ---
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [templateIdToDelete, setTemplateIdToDelete] = useState(null);

  const [isReplace, setIsReplace] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [signPrefixs, setSignPrefixs] = useState([]);
  const [decrees, setDecrees] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);

  const [industries, setIndustries] = useState([]);
  const fetchIndustries = async () => {
    try {
      const response = await industryApi.search("", 0, 100);
      if (response.success) {
        setIndustries(response.data);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchCustomers = useCallback(
    async (keyword = "") => {
      if (customerInfor.CusIsAdmin === false) {
        return;
      }
      setLoadingCustomers(true);
      try {
        const response = await customerApi.searchCustomers(keyword, 0, 100);
        if (response.success && response.data) {
          setAllCustomers(response.data.customers);
        } else {
          toast.error(
            response.message || "Không thể tải danh sách khách hàng.",
          );
        }
      } catch (error) {
        toast.error(
          error.toString() || "Lỗi kết nối khi tải danh sách khách hàng.",
        );
      } finally {
        setLoadingCustomers(false);
      }
    },
    [customerInfor.CusIsAdmin],
  );

  // --- Hàm tìm kiếm ký hiệu ---
  const fetchInvoiceTemplate = async (
    customerId,
    searchKeyword,
    page,
    pageSize,
  ) => {
    if (!customerId) {
      toast.warning("Vui lòng chọn khách hàng.");
      return;
    }
    setLoading(true);
    try {
      const response = await invoiceTemplateApi.searchTemplates(
        customerId,
        searchKeyword,
        page,
        pageSize,
      );
      if (response.success) {
        setInvoiceTemplates(response.data.result);
        setTotalRowCount(response.pagination?.totalRecords || 0);
      } else {
        toast.warning(response.message || "Không tìm thấy dữ liệu ký hiệu.");
        setInvoiceTemplates([]);
        setTotalRowCount(0);
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi tìm kiếm ký hiệu.");
      setInvoiceTemplates([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  };
  const fetchInvoiceTemplateDefault = async () => {
    try {
      const response = await invoiceTemplateApi.LoadDefaultInvoiceTemplate();
      if (response.success) {
        setTemplates(response.data.templates || []);
        setSignPrefixs(response.data.signPrefixs || []);
        setDecrees(response.data.decrees || []);
        setInvoiceTypes(response.data.invoiceTypes || []);
      }
    } catch (error) {
      toast.error(
        error.toString() || "Đã xảy ra lỗi khi tải mẫu hóa đơn mặc định.",
      );
    }
  };

  // --- Phân trang DataGrid ---
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    fetchInvoiceTemplate(
      selectedCustomerId,
      searchKeyword,
      newModel.page,
      newModel.pageSize,
    );
  };

  // --- Thêm ký hiệu mới ---
  const handleAdd = async () => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo && x.CtrNo === "btnAdd" && x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền thêm ký hiệu.");
      return;
    }
    if (!selectedCustomerId) {
      toast.warning(
        "Vui lòng chọn khách hàng ở bộ lọc trước khi thêm mẫu hoá đơn.",
      );
      return;
    }
    let customerBusinessTypeId = customerInfor?.customerBusinessTypeId;
    if (customerBusinessTypeId === null) {
      customerBusinessTypeId = allCustomers.find(
        (c) => c.CusId === selectedCustomerId,
      )?.customerBusinessTypeId;
    }
    setCurrentTemplateToEdit({
      ItLocation: "",
      ItDecree: decrees[0]?.ItDecree || "",
      ItInvoiceType: invoiceTypes[0]?.ItInvoiceType || "",
      ItSignPrefix: signPrefixs[0]?.ItSignPrefix || "",
      ItSignSuffix: "",
      ItSign: "",
      ItTemplate: templates[0]?.ItTemplate || "",
      ItCQT: "",
      CusId: selectedCustomerId,
      ItCreateName: user.UsFullName,
      industryId: null,
      invoiceTemplateBeginNumCqt: null,
      invoiceTemplateLogo: "",
      invoiceTemplateLogoOld: "",
      invoiceTemplateAddress: "",
      invoiceTemplatePhone: "",
      customerBusinessTypeId: customerBusinessTypeId,
      invoiceTemplateHeaderNote: "",
      invoiceTemplateFooterNote: "",
      invoiceTemplateSyncFrom: dayjs(),
      lastTimeGetInfor: dayjs(),
      roundQuantity: 0,
      roundUnitPrice: 0,
      roundAmount: 0,
      roundPercent: 0,
    });
    setFormErrors({});
    setOpenAddEditModal(true);
  };

  const handleAddNewYearInvoiceTemplate = async () => {
    try {
      const response =
        await invoiceTemplateApi.handleAddNewYearInvoiceTemplate();
      if (response.success) {
        toast.success("Thành công");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // --- Sửa ký hiệu ---
  const handleEdit = (itId, isReplace) => {
    if (
      isReplace &&
      !roles.some(
        (x) =>
          x.PvNo === pageNo &&
          x.CtrNo === "btnReplaceSign" &&
          x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền thay thế ký hiệu.");
      return;
    }
    const templateToEdit = invoiceTemplates.find((t) => t.ItId === itId);
    if (templateToEdit) {
      if (isReplace)
        setCurrentTemplateToEdit({
          ...templateToEdit,
          ItCreateDate: dayjs(),
          invoiceTemplateLogoOld: "",
        });
      else
        setCurrentTemplateToEdit({
          ...templateToEdit,
          invoiceTemplateLogoOld: "",
        });
      setFormErrors({});
      setOpenAddEditModal(true);
      setIsReplace(isReplace);
    } else {
      toast.error("Không tìm thấy ký hiệu để chỉnh sửa.");
    }
  };

  // --- Đóng Modal thêm/sửa ---
  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setCurrentTemplateToEdit(null);
    setFormErrors({});
  };

  // --- Lưu ký hiệu (thêm mới hoặc cập nhật) ---
  const handleSave = async (imageLogoFile) => {
    const errors = {};
    if (!currentTemplateToEdit.ItLocation)
      errors.itLocation = "Địa điểm/Máy tính tiền không được để trống";
    if (!currentTemplateToEdit.ItDecree)
      errors.itDecree = "Vui lòng chọn nghị định";
    if (!currentTemplateToEdit.ItInvoiceType)
      errors.itInvoiceType = "Vui lòng chọn loại hóa đơn";
    if (!currentTemplateToEdit.ItSignPrefix)
      errors.itSignPrefix = "Vui lòng chọn ký hiệu tiền tố";
    if (!currentTemplateToEdit.ItSignSuffix)
      errors.itSignSuffix = "Ký hiệu hậu tố không được để trống";
    if (
      currentTemplateToEdit.ItSignSuffix &&
      currentTemplateToEdit.ItSignSuffix.length !== 2
    )
      errors.itSignSuffix = "Ký hiệu hậu tố phải có đúng 2 ký tự";
    if (!currentTemplateToEdit.ItTemplate)
      errors.itTemplate = "Vui lòng chọn mẫu hóa đơn";
    if (!currentTemplateToEdit.ItCQT)
      errors.itCQT = "Mã cơ quan thuế không được để trống";
    if (currentTemplateToEdit.ItCQT && currentTemplateToEdit.ItCQT.length !== 5)
      errors.itCQT = "Mã cơ quan thuế phải có đúng 5 ký tự";
    if (!currentTemplateToEdit.industryId)
      errors.industryId = "Vui lòng chọn ngành nghề";
    if (
      currentTemplateToEdit.invoiceTemplateBeginNumCqt !== null &&
      currentTemplateToEdit.invoiceTemplateBeginNumCqt < 0
    )
      errors.invoiceTemplateBeginNumCqt = "Số bắt đầu phải là số dương";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setLoading(true);
    try {
      let templateData = {
        ItId: currentTemplateToEdit.ItId,
        ItLocation: currentTemplateToEdit.ItLocation,
        ItDecree: currentTemplateToEdit.ItDecree,
        ItInvoiceType: currentTemplateToEdit.ItInvoiceType,
        ItSignPrefix: currentTemplateToEdit.ItSignPrefix,
        ItSignSuffix: currentTemplateToEdit.ItSignSuffix,
        ItTemplate: currentTemplateToEdit.ItTemplate,
        ItCQT: currentTemplateToEdit.ItCQT,
        CusId: selectedCustomerId,
        ItCreateName: user.UsFullName,
        industryId: currentTemplateToEdit.industryId,
        invoiceTemplateBeginNumCqt:
          currentTemplateToEdit.invoiceTemplateBeginNumCqt,
        invoiceTemplateLogo: currentTemplateToEdit.invoiceTemplateLogo,
        invoiceTemplateLogoOld: currentTemplateToEdit.invoiceTemplateLogoOld,
        invoiceTemplateAddress:
          currentTemplateToEdit.invoiceTemplateAddress || "",
        invoiceTemplatePhone: currentTemplateToEdit.invoiceTemplatePhone || "",
        invoiceTemplateHeaderNote:
          currentTemplateToEdit.invoiceTemplateHeaderNote || "",
        invoiceTemplateFooterNote:
          currentTemplateToEdit.invoiceTemplateFooterNote || "",
        invoiceTemplateSyncFrom: currentTemplateToEdit.invoiceTemplateSyncFrom,
        lastTimeGetInfor: currentTemplateToEdit.lastTimeGetInfor,
        roundQuantity: currentTemplateToEdit.roundQuantity,
        roundUnitPrice: currentTemplateToEdit.roundUnitPrice,
        roundAmount: currentTemplateToEdit.roundAmount,
        roundPercent: currentTemplateToEdit.roundPercent,
      };
      let response = {};
      if (isReplace) {
        response = await invoiceTemplateApi.ReplaceInvoiceTemplate(
          templateData,
          imageLogoFile,
        );
      } else
        response = await invoiceTemplateApi.upsertTemplate(
          templateData,
          imageLogoFile,
        );
      if (response.success) {
        toast.success("Lưu thành công!");
        setIsReplace(false);
        handleCloseAddEditModal();
        fetchInvoiceTemplate(
          selectedCustomerId,
          searchKeyword,
          paginationModel.page,
          paginationModel.pageSize,
        ); // Reload data
      } else {
        toast.error(response.message || "Lưu ký hiệu thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi lưu ký hiệu.");
    } finally {
      setLoading(false);
    }
  };

  // --- Mở Modal xác nhận xóa ---
  const handleOpenConfirmDeleteModal = (itId) => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo && x.CtrNo === "btnDelete" && x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền xoá ký hiệu.");
      return;
    }
    setTemplateIdToDelete(itId);
    setOpenConfirmDeleteModal(true);
  };

  // --- Đóng Modal xác nhận xóa ---
  const handleCloseConfirmDeleteModal = () => {
    setTemplateIdToDelete(null);
    setOpenConfirmDeleteModal(false);
  };

  // --- Xóa ký hiệu ---
  const handleDelete = async () => {
    setLoading(true);

    try {
      const response =
        await invoiceTemplateApi.deleteTemplate(templateIdToDelete);
      if (response.success) {
        toast.success(response.message || "Xóa ký hiệu thành công!");
        handleCloseConfirmDeleteModal();
        fetchInvoiceTemplate(
          selectedCustomerId,
          searchKeyword,
          paginationModel.page,
          paginationModel.pageSize,
        ); // Reload data
      } else {
        toast.error(response.message || "Xóa ký hiệu thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi xóa ký hiệu.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnActiveTemplate = async (itId) => {
    setLoading(true);
    try {
      const response = await invoiceTemplateApi.unActiveTemplate(itId);
      if (response.success) {
        fetchInvoiceTemplate(
          selectedCustomerId,
          searchKeyword,
          paginationModel.page,
          paginationModel.pageSize,
        ); // Reload data
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    /*Load tên khách hàng*/
    if (customerInfor.CusIsAdmin) fetchCustomers("");
    /*Tìm kiếm ký hiệu*/
    fetchInvoiceTemplate(
      selectedCustomerId,
      searchKeyword,
      0,
      paginationModel.pageSize,
    );
    /*Load ngành nghề*/
    fetchIndustries();
    /*Load các thuộc tính mặc định*/
    fetchInvoiceTemplateDefault();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* 1. Phần lọc khách hàng */}
        <InvoiceTemplateFilter
          customerIsAdmin={customerInfor.CusIsAdmin}
          fetchCustomers={fetchCustomers}
          allCustomers={allCustomers}
          selectedCustomerId={selectedCustomerId}
          setSelectedCustomerId={setSelectedCustomerId}
          autocompleteInputValue={autocompleteInputValue}
          setAutocompleteInputValue={setAutocompleteInputValue}
          loadingCustomers={loadingCustomers}
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          onSearch={() =>
            fetchInvoiceTemplate(
              selectedCustomerId,
              searchKeyword,
              0,
              paginationModel.pageSize,
            )
          }
        />

        {/* 2. Phần nút chức năng chính */}
        <Grid sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Tạo ký hiệu mới
          </Button>
          {customerInfor.CusId === 1 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNewYearInvoiceTemplate}
              sx={{ ml: 1 }}
            >
              Tạo ký hiệu năm mới cho toàn bộ khách hàng
            </Button>
          )}
        </Grid>

        {/* 3. Phần bảng dữ liệu (DataGrid) */}
        <InvoiceTemplateTable
          rows={invoiceTemplates}
          loading={loading}
          totalRowCount={totalRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          handleEdit={handleEdit}
          handleUnActiveTemplate={handleUnActiveTemplate}
          handleOpenConfirmDeleteModal={handleOpenConfirmDeleteModal}
        />

        {/* 4. Modal Thêm/Sửa */}
        <AddEditInvoiceTemplateModal
          open={openAddEditModal}
          onClose={handleCloseAddEditModal}
          onSave={handleSave}
          loading={loading}
          currentTemplateToEdit={currentTemplateToEdit}
          setCurrentTemplateToEdit={setCurrentTemplateToEdit}
          isReplace={isReplace}
          formErrors={formErrors}
          decrees={decrees}
          invoiceTypes={invoiceTypes}
          signPrefixs={signPrefixs}
          templates={templates}
          industries={industries}
        />

        {/* 5. Modal Xác nhận xóa */}
        <Dialog open={openConfirmDeleteModal} maxWidth="xs" fullWidth>
          <DialogTitle>Xác nhận xóa</DialogTitle>
          <DialogContent>
            <Typography>
              Bạn có chắc chắn muốn xóa ký hiệu này không?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDeleteModal} color="primary">
              Hủy
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Xoá
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default InvoiceSignAndTemplatePage;
